// src/components/roles/RoleFilters/RoleFilters.tsx
import React from 'react';
import { Box, MenuItem } from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { RoleFilter } from '../../../types/role.types';
import { useRoleFilters, UseRoleFiltersProps } from './RoleFilters.hooks';
import {
  FilterContainer,
  FilterRow,
  SecondaryFilterRow,
  StyledTextField,
  FilterCollapse,
} from './RoleFilters.styles';
import {
  FilterToolbarGradientButton,
  FilterToolbarOutlinedButton,
} from '../../../styles/theme/filterToolbarButtons';
import { ListPageActiveFilters } from '../../common/list-page';

export interface RoleFiltersProps extends UseRoleFiltersProps {
  filters: RoleFilter;
  className?: string;
}

const RoleFiltersComponent: React.FC<RoleFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onSearch,
  onReset,
  onToggleMoreFilters,
  className,
}) => {
  const {
    handleInputChange,
    handleSelectChange,
    handleKeyPress,
    handleDateChange,
    renderStatusValue,
    renderSortByValue,
    activeFilterChips,
  } = useRoleFilters({
    filters,
    setFilters,
    appliedFilters,
    onClearAppliedField,
    showMoreFilters,
    onSearch,
    onReset,
    onToggleMoreFilters,
  });

  return (
    <FilterContainer className={className}>
      {/* Primera fila - Campo de búsqueda y botones */}
      <FilterRow>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Buscar Rol"
            name="searchTerm"
            value={filters.searchTerm || ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Buscar por nombre o descripción"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />
        </Box>

        <FilterToolbarOutlinedButton
          variant="outlined"
          size="medium"
          onClick={onToggleMoreFilters}
          startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showMoreFilters ? 'Menos' : 'Más'}
        </FilterToolbarOutlinedButton>

        <FilterToolbarOutlinedButton
          variant="outlined"
          size="medium"
          onClick={onReset}
          startIcon={<RefreshIcon />}
        >
          Reiniciar
        </FilterToolbarOutlinedButton>

        <FilterToolbarGradientButton
          variant="contained"
          size="medium"
          onClick={onSearch}
          startIcon={<SearchIcon />}
        >
          Buscar
        </FilterToolbarGradientButton>
      </FilterRow>

      {/* Filtros adicionales - Todos en la misma fila */}
      <FilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <SecondaryFilterRow>
          {/* Estado */}
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Estado"
            name="status"
            value={filters.status || 'all'}
            onChange={handleSelectChange}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, renderValue: renderStatusValue }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Activo</MenuItem>
            <MenuItem value="blocked">Bloqueado</MenuItem>
          </StyledTextField>

          {/* Fecha inicio */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Fecha Inicio"
            name="startDate"
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                max: filters.endDate || undefined,
              },
            }}
          />

          {/* Fecha fin */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Fecha Fin"
            name="endDate"
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                min: filters.startDate || undefined,
              },
            }}
          />

          {/* Ordenar por */}
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Ordenar por"
            name="sortBy"
            value={filters.sortBy || ''}
            onChange={handleSelectChange}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, renderValue: renderSortByValue }}
          >
            <MenuItem value="">Sin ordenar</MenuItem>
            <MenuItem value="name">Nombre</MenuItem>
            <MenuItem value="createdAt">Fecha de creación</MenuItem>
            <MenuItem value="userCount">Número de usuarios</MenuItem>
          </StyledTextField>
        </SecondaryFilterRow>
      </FilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </FilterContainer>
  );
};

export default React.memo(RoleFiltersComponent);
